import type { PostTryOnJourney } from '../model/post-tryon'

const genericWhatsAppAppUrl = 'whatsapp://'
const supportedHttpsHosts = new Set(['wa.me', 'api.whatsapp.com', 'web.whatsapp.com'])
const phonePattern = /^[0-9]{8,15}$/
const maxMessageLength = 1_000

type ParsedWhatsAppTarget = {
  phone?: string
  text?: string
  sourceUrl: string
}

function normalizedPhone(value: string | null) {
  const phone = (value || '').trim().replace(/^\+/, '')
  return phonePattern.test(phone) ? phone : undefined
}

function normalizedText(value: string | null) {
  const text = (value || '').trim()
  if (!text) return undefined
  return text.slice(0, maxMessageLength)
}

function parseWhatsAppTarget(value?: string): ParsedWhatsAppTarget | undefined {
  if (!value) return undefined
  try {
    const parsed = new URL(value)
    if (parsed.username || parsed.password) return undefined
    if (parsed.protocol === 'whatsapp:' && parsed.hostname === 'send') {
      const phone = normalizedPhone(parsed.searchParams.get('phone'))
      if (!phone) return undefined
      return { phone, text: normalizedText(parsed.searchParams.get('text')), sourceUrl: value }
    }
    if (parsed.protocol !== 'https:' || !supportedHttpsHosts.has(parsed.hostname.toLowerCase())) return undefined
    const host = parsed.hostname.toLowerCase()
    if (host === 'wa.me') {
      const segments = parsed.pathname.split('/').filter(Boolean)
      if (segments.length !== 1) return undefined
      const phone = normalizedPhone(segments[0] || '')
      if (!phone) return undefined
      return { phone, text: normalizedText(parsed.searchParams.get('text')), sourceUrl: value }
    }
    if (parsed.pathname.replace(/\/+$/, '') !== '/send') return undefined
    const phone = normalizedPhone(parsed.searchParams.get('phone'))
    if (!phone) return undefined
    return { phone, text: normalizedText(parsed.searchParams.get('text')), sourceUrl: value }
  } catch {
    return undefined
  }
}

function nativeWhatsAppUrl(target?: ParsedWhatsAppTarget) {
  if (!target?.phone) return genericWhatsAppAppUrl
  const parameters = new URLSearchParams({ phone: target.phone })
  if (target.text) parameters.set('text', target.text)
  return `whatsapp://send?${parameters.toString()}`
}

export function isSafeWhatsAppReturnUrl(value?: string) {
  return Boolean(parseWhatsAppTarget(value))
}

export function isSafeWhatsAppNavigationUrl(value?: string) {
  return value === genericWhatsAppAppUrl || isSafeWhatsAppReturnUrl(value)
}

export function getWhatsAppReturnUrl(journey: PostTryOnJourney) {
  const configured = journey.returnToWhatsApp?.url
  return parseWhatsAppTarget(configured)?.sourceUrl || genericWhatsAppAppUrl
}

export function getWhatsAppAppUrl(journey: PostTryOnJourney) {
  return nativeWhatsAppUrl(parseWhatsAppTarget(journey.returnToWhatsApp?.url))
}

export function hasConfiguredWhatsAppReturnUrl(journey: PostTryOnJourney) {
  return Boolean(parseWhatsAppTarget(journey.returnToWhatsApp?.url))
}
